import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react';
import React, { FC, Fragment, useEffect, useState } from 'react';
import i18n from '@/i18n';
import { getUserId, isProOrSgp } from '@/infrastructure/auth';
import { CreateDefectModal } from '@/presentation/components/defect-info/create-defect-modal';
import { BindTapdModal } from '@/presentation/components/tapd-info/bind-tapd-modal';
import { CreateTapdModal } from '@/presentation/components/tapd-info/create-tapd-modal';
import { EditTapdModal, EditType } from '@/presentation/components/tapd-info/edit-tapd-modal';
import { ReleaseTapdModal } from '@/presentation/components/tapd-info/release-tapd-modal';
import { TapdData, TapdStatusTextRender } from '@/presentation/components/tapd-info/tapd-text';
import { stores } from '@/presentation/store';
import { FIELD_ALL, FieldKey, FieldScope, getLocalFieldLabelByValue, Option, Zone } from '@/proto/field';

export const DEFECT_PLATFORM_NAME = isProOrSgp() ? i18n.t('缺陷') : 'TAPD';

export type TapdParams = {
  type: Zone,
  issueKey: string;
};

const priorityList = [
  { label: i18n.t('紧急'), value: 'urgent' },
  { label: i18n.t('高'), value: 'high' },
  { label: i18n.t('中'), value: 'medium' },
  { label: i18n.t('低'), value: 'low' },
  { label: i18n.t('无关紧要'), value: 'insignificant' },
];

export const getInitFields = (appVersionOptions?: Option[]) => ([
  {
    name: 'version_report',
    label: i18n.t('发现版本'),
    value: '',
    type: 'autoComplete',
    options: appVersionOptions?.map(item => ({ ...item, value: item.label })) ?? [],
  },
  {
    name: 'reporter',
    label: i18n.t('创建人'),
    value: getUserId(),
    type: 'userSelect',
  },
  {
    name: 'current_owner',
    label: i18n.t('处理人'),
    value: '',
    type: 'userSelect',
  },
  {
    name: 'priority',
    label: i18n.t('优先级'),
    value: priorityList[0].value,
    options: priorityList,
    type: 'select',
  },
]);

export const initVersionReportData = getInitFields()[0];
export const initReporterData = getInitFields()[1];
export const initCurrentOwnerData = getInitFields()[2];

export const getTapdTitleKey = (type: Zone) => {
  const titleKey: Record<string, string> = {
    [Zone.Lag]: i18n.t(' 【卡顿问题】 '),
    [Zone.Launch]: i18n.t(' 【启动问题】 '),
    [Zone.Compliance]: i18n.t(' 【合规问题】 '),
    [Zone.Foom]: i18n.t(' 【FOOM问题】 '),
    [Zone.FoomMalloc]: i18n.t(' 【FOOM内存分配】 '),
    [Zone.JavaLeak]: i18n.t(' 【Java内存泄漏问题】 '),
    [Zone.VcLeak]: i18n.t(' 【VC泄漏问题】 '),
    [Zone.BigPic]: i18n.t(' 【大图分析问题】 '),
    [Zone.BadMalloc]: i18n.t(' 【大块分配内存问题】 '),
    [Zone.Fd]: i18n.t(' 【FD触顶问题】 '),
    [Zone.Anr]: i18n.t(' 【ANR问题】 '),
    [Zone.MemGraph]: i18n.t('【内存详情】'),
    [Zone.PowerAnomalyAnalysis]: i18n.t('【耗电问题】'),
    [Zone.Crash]: i18n.t('【CRASH问题】'),
    [Zone.Error]: i18n.t('【ERROR问题】'),
    [Zone.AndroidANR]: i18n.t('【ANR问题】'),
    [Zone.AndroidFoom]: i18n.t('【FOOM问题】'),
    [Zone.MemDetail]: i18n.t('【内存详情问题】'),
  };
  return titleKey[type];
};

export const getTapdTitleMethod = (type: Zone, keyMethods: string[]) => {
  if (isEmpty(keyMethods)) {
    return '';
  }
  if (([Zone.Crash, Zone.Anr, Zone.Error, Zone.MemDetail,
    Zone.AndroidANR, Zone.AndroidFoom].includes(type)) && keyMethods.length >= 1) {
    return keyMethods[0];
  }
  if (type === Zone.JavaLeak && keyMethods.length >= 1) {
    return keyMethods[0];
  }
  if (type === Zone.Fd && keyMethods.length >= 2) {
    return `${keyMethods[0]} ${keyMethods[1]}`;
  }
  return keyMethods[keyMethods.length - 1];
};

export const getTapdTitleCompliance = (problemType: string, privacyType: string, privacyFunc: string) => `${getLocalFieldLabelByValue(FieldKey.ProblemType, problemType)} - ${getLocalFieldLabelByValue(FieldKey.PrivacyType, privacyType)} - ${privacyFunc}`;

export const getTapdTitleMethodBatch = (type: Zone, row: Record<string, any>) => {
  if (type === Zone.Compliance) {
    return getTapdTitleCompliance(row.problemType, row.privacyType, row.privacyFunc);
  }
  return getTapdTitleMethod(type, row.keyMethodList);
};

export const getAppVersionInfo = (scope: FieldScope) => {
  const appVersionList = stores.FieldStore.getField(FieldKey.AppVersion, scope)
    .filter(value => value.value !== FIELD_ALL);
  if (appVersionList?.length > 0) {
    return {
      value: appVersionList[0].value ?? '',
      options: appVersionList,
    };
  }
  return {
    value: '',
    options: [],
  };
};

export const TapdInfo: FC<{
  exceptionName?: string;
  pageId: string;
  defaultAppVersion?: string;
  appVersionOptions: Option[];
  feature: string;
  keyMethods: string[];
  getKeyMethods?: () => Promise<string[]>;
  tapdData: TapdData;
  issueUrl: string;
  tapdSubmit: () => void;
  type: Zone;
  title?: string;
  content?: string;
  defaultTapdHandler?: string;
}> = observer(({
  pageId, defaultAppVersion, appVersionOptions, feature,
  keyMethods, tapdData, issueUrl,
  tapdSubmit, type, title, content, defaultTapdHandler,
  getKeyMethods, exceptionName,
}) => {
  const [createTapdVisible, setCreateTapdVisible] = useState(false);
  const [editTapdVisible, setEditTapdVisible] = useState(false);
  const [releaseTapdVisible, setReleaseTapdVisible] = useState(false);
  const [bindTapdVisible, setBindTapdVisible] = useState(false);
  const [workspaceId, setWorkspaceId] = useState('');
  const [editType, setEditType] = useState<EditType>('add');
  const [mode, setMode] = useState('');
  const [innerKeyMethods, setInnerKeyMethods] = useState(keyMethods);

  const initKeyMethods = async () => {
    if (!getKeyMethods) return;

    const keyMethods = await getKeyMethods();
    setInnerKeyMethods(keyMethods);
  };

  const handleTapdSubmit = () => {
    setCreateTapdVisible(true);
  };
  useEffect(() => {
    setInnerKeyMethods(keyMethods);
  }, [keyMethods]);

  useEffect(() => {
    createTapdVisible && initKeyMethods();
  }, [createTapdVisible]);

  return (
    <Fragment>
      <TapdStatusTextRender
        tapdData={tapdData}
        tapdSubmit={handleTapdSubmit}
        pageId={pageId}
        deleteTapd={() => setReleaseTapdVisible(true)}
        updateTapd={() => setBindTapdVisible(true)}
      />
      {isProOrSgp() ? (
        <CreateDefectModal
          type={type}
          title={title ?? `${getTapdTitleKey(type)}${exceptionName ? exceptionName : getTapdTitleMethod(type, innerKeyMethods)}`}
          content={content ?? `${innerKeyMethods.join('\n')}\nIssue ID：${feature}\n`}
          issueKey={feature}
          issueUrl={issueUrl}
          pageId={pageId}
          visible={createTapdVisible}
          onClose={() => setCreateTapdVisible(false)}
          onChangeId={(workspaceId: string) => setWorkspaceId(workspaceId)}
          onSubmit={tapdSubmit}
          defaultAppVersion={defaultAppVersion ?? ''}
        />
      ) : (
        <CreateTapdModal
          defaultTapdHandler={defaultTapdHandler}
          type={type}
          title={title ?? `${getTapdTitleKey(type)}${exceptionName ? exceptionName : getTapdTitleMethod(type, innerKeyMethods)}`}
          content={content ?? `${innerKeyMethods.join('\n')}\nIssue ID：${feature}\n`}
          issueKey={feature}
          issueUrl={issueUrl}
          pageId={pageId}
          defaultAppVersion={defaultAppVersion}
          appVersionOptions={appVersionOptions}
          visible={createTapdVisible}
          onClose={() => setCreateTapdVisible(false)}
          onChangeId={(workspaceId: string) => setWorkspaceId(workspaceId)}
          openEidtModal={(editType, mode) => {
            setEditTapdVisible(true);
            setEditType(editType);
            setMode(mode);
          }}
          onSubmit={tapdSubmit}
        />
      )}
      <EditTapdModal
        visible={editTapdVisible}
        workspaceId={workspaceId}
        editType={editType}
        mode={mode}
        appVersionOptions={appVersionOptions}
        onHideEdit={() => setEditTapdVisible(false)}
        openCreate={() => setCreateTapdVisible(true)}
      />
      <ReleaseTapdModal
        visible={releaseTapdVisible}
        releaseParams={{
          type,
          issueKey: feature,
        }}
        onHide={() => setReleaseTapdVisible(false)}
        onSubmit={tapdSubmit}
      />
      <BindTapdModal
        visible={bindTapdVisible}
        bindParams={{
          type,
          issueKey: feature,
        }}
        onHide={() => setBindTapdVisible(false)}
        onSubmit={tapdSubmit}
      />
    </Fragment>
  );
});
